import { ChangeDetectorRef, Component, computed, ElementRef, OnInit, signal, ViewChild, effect, untracked } from '@angular/core';
import { PropertyChargeScheduleModel } from '../../../../core/models/data/property-charge-schedule.model';
import { PropertyChargeScheduleListSortOptions } from '../../../../core/enums/sort-options.enums';
import { ApiSettings, LocalStorageKeyConstants, PaginationConstants } from '../../../../core/constants/constants';
import { ConfirmationModalComponent } from '../../../../parts/confirmation-modal/confirmation-modal.component';
import { InfoModalComponent } from '../../../../parts/info-modal/info-modal.component';
import { ChargesService } from '../../../../core/services/charge/charge.service';
import { BaseListPageClass } from '../../../../core/base-classes/base-list-page-class';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { ListConstants } from '../../../../core/constants/lists.constants';
import { PropertyStatuses, ScheduleFrequency, ChartOfAccountCategory, SelectionPrimaryKeys } from '../../../../core/enums/data.enums';
import { CurrencyModel } from '../../../../core/models/data/currency.model';
import { ChartOfAccountModel } from '../../../../core/models/data/chart-of-account.model';
import { PaginatedListModel } from '../../../../core/models/service/paginated-list.model';
import { ServiceResultModel } from '../../../../core/models/service/service-result.model';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { InViewPortDirective } from '../../../../core/directives/in-view-port.directive';
import { PaginationComponent } from '../../../../parts/pagination/pagination.component';
import { SortDropdownComponent } from '../../../../parts/sort-dropdown/sort-dropdown/sort-dropdown';
import { NoValuePipe } from "../../../../core/pipes/no-value/no-value.pipe";
import { OrgDatePipe } from '../../../../core/pipes/org-date/org-date.pipe';
import { MobileCardCheckbox } from '../../../../parts/mobile-card-checkbox/mobile-card-checkbox';
import { RouterLink } from '@angular/router';
import { ItemSelectionPipe } from '../../../../core/pipes/item-selection/item-selection-pipe';
import { StatusBadgeComponent } from '../../../../parts/status-badge/status-badge.component';
import { ScheduleFrequencyPipe } from '../../../../core/pipes/schedule-frequency/schedule-frequency.pipe';
import { PropertyChargeScheduleSearchFilter } from '../property-charge-schedule-search-filter/property-charge-schedule-search-filter';
import { CurrencyDisplayPipe } from '../../../../core/pipes/currency/currency.pipe';
import { BalancesMessagingConstants } from '../../../../core/constants/messaging.constants';
import { DisableWithoutPermissionDirective } from '../../../../core/directives/disable-without-permission/disable-without-permission.directive';
import { BalancesPermission } from '../../../../core/enums/permissions.enums';
import { ResponsiveTableDirective } from '../../../../core/directives/responsive-table/responsive-table.directive';
import { ResponsiveTableColumnDirective } from '../../../../core/directives/responsive-table/responsive-table-column.directive';
import { CloseMenuOnSettleDirective } from '../../../../core/directives/close-menu-on-settle.directive';
import { DebounceClickDirective } from '../../../../core/directives/debounce-click.directive';
import { TooltipDirective, TooltipPlacement } from '../../../../core/directives/tooltip.directive';
import { BulkActionRequest } from '../../../../core/models/service/bulk-action-request.model';
import { PropertyChargeSchedulesFilter } from '../../../../core/filters/property-charge-schedule.filter';
import { FileDownloadUtilities } from '../../../../core/utilities/file-download.utilities';
import { DeleteInvoiceScheduleModalComponent } from '../delete-invoice-schedule-modal/delete-invoice-schedule-modal.component';
import { ReactivateInvoiceScheduleModalComponent } from '../reactivate-invoice-schedule-modal/reactivate-invoice-schedule-modal.component';
import { ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { UpdatePropertyChargeScheduleRequest } from '../../../../core/models/data/property-charge-schedule.model';
import { PageTitleComponent } from '../../../../parts/page-title/page-title.component';
import { StripeSetupNoticeComponent } from '../../../../parts/stripe-setup-notice/stripe-setup-notice.component';

@Component({
  selector: 'ms-property-charge-schedule-list',
  imports: [
    PageTitleComponent,
    CommonModule,
    FormsModule,
    FontAwesomeModule,
    InViewPortDirective,
    PaginationComponent,
    SortDropdownComponent,
    NoValuePipe,
    OrgDatePipe,
    MobileCardCheckbox,
    RouterLink,
    ConfirmationModalComponent,
    InfoModalComponent,
    ItemSelectionPipe,
    StatusBadgeComponent,
    PropertyChargeScheduleSearchFilter,
    CurrencyDisplayPipe,
    ScheduleFrequencyPipe,
    DisableWithoutPermissionDirective,
    ResponsiveTableDirective,
    ResponsiveTableColumnDirective,
    CloseMenuOnSettleDirective,
    DebounceClickDirective,
    TooltipDirective,
    DeleteInvoiceScheduleModalComponent,
    ReactivateInvoiceScheduleModalComponent,
    StripeSetupNoticeComponent
  ],
  templateUrl: './property-charge-schedule-list.component.html',
  host: {"style":"display:contents"}
})
export class PropertyChargeScheduleListComponent extends BaseListPageClass implements OnInit {
  public isDownloading = signal<boolean>(false);
  // Separate from isDownloading so a filtered export doesn't make every row read as downloading.
  public downloadingScheduleId = signal<number | null>(null);
  // Both exports hit the same endpoint, so either one blocks the other; the signals above stay
  // apart only to put the spinner on whichever row or list item started the download.
  public isAnyDownloadInProgress = computed(() => this.isDownloading() || this.downloadingScheduleId() !== null);
  public downloadTooltip = BalancesMessagingConstants.PROPERTY_CHARGE_SCHEDULE_DOWNLOAD_TOOLTIP();
  public hasSchedulesSelected = computed(() => this.isSelectAll || this.selectionItems().length > 0);

  //#region Properties & Signals
  public propertyChargeScheduleList = signal<PropertyChargeScheduleModel[]>([]);
    
  // Enums
  public PropertyChargeScheduleListSortOptions = PropertyChargeScheduleListSortOptions;
  public PropertyStatuses = PropertyStatuses;
  public ScheduleFrequency = ScheduleFrequency;
  public BalancesPermissions = BalancesPermission;
  public SelectionPrimaryKeys = SelectionPrimaryKeys;
  public TooltipPlacement = TooltipPlacement;
  
  public skeletonPropertyChargeSchedules: PropertyChargeScheduleModel[] = [];
  @ViewChild(PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_NAME().replace(/^#/, '')) jumpToElementRef!: ElementRef;
  @ViewChild(PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_MOBILE_NAME().replace(/^#/, '')) jumpToElementMobileRef!: ElementRef;
  
  @ViewChild('propertyChargeScheduleListInfoModalRef') propertyChargeScheduleListInfoModalRef!: InfoModalComponent;
  @ViewChild('propertyChargeScheduleListConfirmationModalRef') propertyChargeScheduleListConfirmationModalRef!: ConfirmationModalComponent;
  @ViewChild('deleteInvoiceScheduleModalRef') deleteInvoiceScheduleModalRef!: DeleteInvoiceScheduleModalComponent;
  @ViewChild('reactivateInvoiceScheduleModalRef') reactivateInvoiceScheduleModalRef!: ReactivateInvoiceScheduleModalComponent;

  public targetedSchedule = signal<PropertyChargeScheduleModel | null>(null);
  public isProcessingActivationChange = signal<boolean>(false);
  
  constructor(
    private chargesService: ChargesService,
    private changeDetector: ChangeDetectorRef,
    public userSessionService: UserSessionService,
    public bootstrapService: BootstrapService
  ) {
    super();

    effect(() => {
      const org = this.userSessionService.activeOrganization();

      if (org && this.userSessionService.hasInitializedOrganization()) {
        untracked(() => {
          this.loadPropertyChargeSchedules(false);
        });
      }
    });
  }
  
  
  public ngOnInit(): void {
    this.createSkeletonPropertyChargeSchedules();
    this.orderBy = this.userSessionService.propertyChargeSchedulesOrderBy;
    this.sortOptions = [...ListConstants.PROPERTY_CHARGE_SCHEDULES_SORT_OPTIONS()];
    //Instead of loadPropertyChargeSchedules being called here we are calling it from the filter control
    // then firing it's filterApplied event since we are wanting default filters on the initial page load
  }

  private createSkeletonPropertyChargeSchedules(): void {
    this.skeletonPropertyChargeSchedules = Array.from({ length: this.take }, (_, index) => ({
      propertyChargeScheduleId: index,
      organizationId: -1,
      name: 'Loading...',
      description: 'loading',
      scheduledAmount: -1,
      scheduledCurrency: <CurrencyModel>{
        currencyId: -1,
        name: 'loading',
        code: 'loading',
        symbol: 'loading',
        minorUnits: -1
      },
      memo: 'loading',
      revenueAccount: <ChartOfAccountModel>{
        chartOfAccountId: -1,
        organizationId: -1,
        accountNumber: -1,
        category: ChartOfAccountCategory.Revenue,
        name: 'loading',
        description: 'loading',
        isActive: true
      },
      assetAccount: <ChartOfAccountModel>{
        chartOfAccountId: -1,
        organizationId: -1,
        accountNumber: -1,
        category: ChartOfAccountCategory.Assets,
        name: 'loading',
        description: 'loading',
        isActive: true
      },
      lastModified: '6/1/2025',
      status: PropertyStatuses.Active,
      frequency: ScheduleFrequency.Once,
      firstIssuanceDate: 'loading',
      sendInvoiceAtEndOfMonth: false,
      invoiceDueNumberOfMonthsFromIssuance: -1,
      invoiceDueDayOfMonth: -1,
      invoiceDueAtEndOfMonth: false,
      scheduleEnd: 'loading',
      propertiesCount: -1,
      lateChargesCount: -1
    } as PropertyChargeScheduleModel));
  }

  //#endregion Properties & Signals

  //#region Data Loading
  public get displayPropertyChargeSchedules(): PropertyChargeScheduleModel[] {
    if (this.isLoading && (!this.propertyChargeScheduleList() || this.propertyChargeScheduleList().length === 0)) {
      return this.skeletonPropertyChargeSchedules;
    }
    return this.propertyChargeScheduleList() || [];
  }

  public async loadPropertyChargeSchedules(append: boolean = false): Promise<void> {
    this.isLoading = true;

    if (append === false) { this.resetPagination(); }
    
    const activeOrganization = this.userSessionService.activeOrganization();
    const organizationId = activeOrganization?.organizationId;
    this.userSessionService.propertyChargeSchedulesFilter.organizationIds = organizationId ? [organizationId] : [];

    const orgSettings = await this.userSessionService.getOrganizationSettings();
    this.userSessionService.propertyChargeSchedulesFilter.timeZoneId = 
      orgSettings?.timeZoneId ?? ApiSettings.DEFAULT_ORG_TIMEZONE_FALLBACK().id;

    const response: ServiceResultModel<PaginatedListModel<PropertyChargeScheduleModel>> = 
      await this.chargesService.searchPropertyChargeSchedules(
        this.userSessionService.propertyChargeSchedulesFilter, 
        this.orderBy, 
        this.skip(), 
        this.take
      );
        
    this.totalCount = response?.data?.total ?? 0;
    this.hasNext = response?.data?.hasNext ?? false;

    if (response?.data?.data) {
      if (append == true) {        
        this.propertyChargeScheduleList.set(this.propertyChargeScheduleList().concat(response.data.data));
      } else {      
        this.propertyChargeScheduleList.set(response.data.data);
        //Clear Selected Items
        this.isSelectAll = false;
        this.selectionItems.set([]);
      }
    }
      
    this.isLoading = false;
    this.calculateSelected();
    this.changeDetector.detectChanges();    
  }

  public async loadMore(): Promise<void> {
    this.loadPropertyChargeSchedules(true);
  }

  public filterApplied(): void {
    this.loadPropertyChargeSchedules();
  }

  public loadMoreInView(): void {       
    if (this.canLoadMore && this.hasNext) {
      this.loadMore();
      this.canLoadMore = false;
      this.loadMoreDebounce();
    }
  }

  public clearSearch(): void {
    this.userSessionService.propertyChargeSchedulesFilter.searchTerm = "";
  }
  //#endregion Data Loading

  //#region Sorting & Pagination

  public toggleSortDirection(columnName: string): void {
    const columnSortMap: Record<string, [PropertyChargeScheduleListSortOptions, PropertyChargeScheduleListSortOptions]> = {
      "Name": [PropertyChargeScheduleListSortOptions.NameAsc, PropertyChargeScheduleListSortOptions.NameDesc],
      "Properties Count": [PropertyChargeScheduleListSortOptions.PropertyCountAsc, PropertyChargeScheduleListSortOptions.PropertyCountDesc],
      "Scheduled Amount": [PropertyChargeScheduleListSortOptions.ScheduledAmountAsc, PropertyChargeScheduleListSortOptions.ScheduledAmountDesc],
      "Frequency": [PropertyChargeScheduleListSortOptions.FrequencyAsc, PropertyChargeScheduleListSortOptions.FrequencyDesc],
      "Late Charges": [PropertyChargeScheduleListSortOptions.LateChargesAsc, PropertyChargeScheduleListSortOptions.LateChargesDesc],
      "Status": [PropertyChargeScheduleListSortOptions.StatusAsc, PropertyChargeScheduleListSortOptions.StatusDesc],
      "Modified": [PropertyChargeScheduleListSortOptions.LastModifiedAsc, PropertyChargeScheduleListSortOptions.LastModifiedDesc],
    };
    
    const [ascOption, descOption] = columnSortMap[columnName]
    || [PropertyChargeScheduleListSortOptions.NameAsc, PropertyChargeScheduleListSortOptions.NameDesc];
    
    let sortDirection: PropertyChargeScheduleListSortOptions;
    
    if (this.orderBy === descOption) {
      sortDirection = ascOption;
    } else if (this.orderBy === ascOption) {
      sortDirection = descOption;
    } else {
      sortDirection = ascOption;
    }
    
    this.setSortOrder(sortDirection);
  }
  
  public setSortOrder(sortOption: string) {
    this.orderBy = sortOption;
    this.userSessionService.propertyChargeSchedulesOrderBy = this.orderBy;
    this.userSessionService.saveToLocalStorage(LocalStorageKeyConstants.PROPERTY_CHARGE_SCHEDULES_ORDERBY(), this.orderBy);
    this.filterApplied();
  }

  public jumpToTop(element: string) : void {
    switch (element) {
      case PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_NAME():
        this.jumpToElementRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
        break;

      case PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_MOBILE_NAME():
        this.jumpToElementMobileRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
        break;
    }
  }
  
  //#endregion Sorting & Pagination

  //#region Export

  public async downloadFilteredInvoiceData(): Promise<void> {
    if (this.isAnyDownloadInProgress()) return;
    if (!this.isSelectAll && this.selectionItems().length === 0) return;

    this.isDownloading.set(true);

    try {
      const request = this.buildScheduleBulkActionRequest();
      const response = await this.chargesService.exportChargeScheduleInvoiceData(request);

      if (response.success && response.data) {
        FileDownloadUtilities.downloadBlob(response.data, FileDownloadUtilities.buildInvoiceDataFilename());
      }
    } finally {
      this.isDownloading.set(false);
    }
  }

  public async downloadSingleScheduleInvoiceData(scheduleId: number): Promise<void> {
    if (this.isAnyDownloadInProgress()) return;

    this.downloadingScheduleId.set(scheduleId);

    try {
      const response = await this.chargesService.exportChargeScheduleInvoiceData({
        useFilter: false,
        selectedIds: [scheduleId],
        filter: null,
        excludedIds: null,
        note: null
      });

      if (response.success && response.data) {
        FileDownloadUtilities.downloadBlob(response.data, FileDownloadUtilities.buildInvoiceDataFilename());
      }
    } finally {
      this.downloadingScheduleId.set(null);
    }
  }

  private buildScheduleBulkActionRequest(): BulkActionRequest<PropertyChargeSchedulesFilter> {
    if (this.isSelectAll) {
      return {
        useFilter: true,
        excludedIds: [...this.selectionItems()],
        filter: this.userSessionService.propertyChargeSchedulesFilter,
        selectedIds: null,
        note: null
      };
    } else {
      return {
        useFilter: false,
        selectedIds: [...this.selectionItems()],
        filter: null,
        excludedIds: null,
        note: null
      };
    }
  }

  //#endregion Export

  //#region Delete

  public openDeleteScheduleModal(schedule: PropertyChargeScheduleModel): void {
    this.deleteInvoiceScheduleModalRef.open(schedule.propertyChargeScheduleId, schedule.name);
  }

  public onScheduleDeleted(): void {
    this.loadPropertyChargeSchedules(false);
  }

  //#endregion Delete

  //#region Deactivate / Reactivate

  public openDeactivateConfirmationModal(schedule: PropertyChargeScheduleModel): void {
    this.targetedSchedule.set(schedule);

    this.propertyChargeScheduleListConfirmationModalRef.modalType.set(ModalTypes.Question);
    this.propertyChargeScheduleListConfirmationModalRef.message.set(
      BalancesMessagingConstants.PROPERTY_CHARGE_SCHEDULE_DEACTIVATE_CONFIRMATION()
    );
    this.propertyChargeScheduleListConfirmationModalRef.enableNotes.set(false);
    this.propertyChargeScheduleListConfirmationModalRef.isNoteRequired.set(false);
    this.propertyChargeScheduleListConfirmationModalRef.enableTypeToConfirm.set(false);
    this.propertyChargeScheduleListConfirmationModalRef.confirmButtonText.set(
      BalancesMessagingConstants.PROPERTY_CHARGE_SCHEDULE_DEACTIVATE_ACTION_LABEL()
    );
    this.propertyChargeScheduleListConfirmationModalRef.confirmButtonClassName.set('btn-danger');

    this.bootstrapService.manageModal('#propertyChargeScheduleListConfirmationModal', ModalActions.Show);
  }

  public async onDeactivateConfirmed(): Promise<void> {
    const schedule = this.targetedSchedule();
    if (!schedule || this.isProcessingActivationChange()) return;

    this.isProcessingActivationChange.set(true);
    this.propertyChargeScheduleListConfirmationModalRef.isSubmitting.set(true);

    try {
      const fullScheduleResponse = await this.chargesService.getPropertyChargeScheduleById(
        schedule.propertyChargeScheduleId,
        true
      );

      if (!fullScheduleResponse.success || !fullScheduleResponse.data) {
        this.showDeactivateFailure(fullScheduleResponse.message);
        return;
      }

      const request = this.buildDeactivateRequest(fullScheduleResponse.data);
      const result = await this.chargesService.updatePropertyChargeSchedule(
        schedule.propertyChargeScheduleId,
        request
      );

      this.bootstrapService.manageModal('#propertyChargeScheduleListConfirmationModal', ModalActions.Hide);

      if (result.success) {
        this.propertyChargeScheduleListInfoModalRef.modalType.set(ModalTypes.Success);
        this.propertyChargeScheduleListInfoModalRef.message.set(
          BalancesMessagingConstants.PROPERTY_CHARGE_SCHEDULE_DEACTIVATE_SUCCESS()
        );
        this.bootstrapService.manageModal('#propertyChargeScheduleListInfoModal', ModalActions.Show);
        this.loadPropertyChargeSchedules(false);
      } else {
        this.showDeactivateFailure(result.message);
      }
    } finally {
      this.isProcessingActivationChange.set(false);
      this.propertyChargeScheduleListConfirmationModalRef.isSubmitting.set(false);
    }
  }

  public onDeactivateCancelled(): void {
    this.bootstrapService.manageModal('#propertyChargeScheduleListConfirmationModal', ModalActions.Hide);
    this.targetedSchedule.set(null);
  }

  public openReactivateModal(schedule: PropertyChargeScheduleModel): void {
    this.targetedSchedule.set(schedule);
    this.reactivateInvoiceScheduleModalRef.open(schedule);
  }

  public onScheduleReactivated(): void {
    this.targetedSchedule.set(null);
    this.loadPropertyChargeSchedules(false);
  }

  private showDeactivateFailure(message?: string): void {
    this.bootstrapService.manageModal('#propertyChargeScheduleListConfirmationModal', ModalActions.Hide);
    this.propertyChargeScheduleListInfoModalRef.modalType.set(ModalTypes.Failure);
    this.propertyChargeScheduleListInfoModalRef.message.set(
      message || BalancesMessagingConstants.PROPERTY_CHARGE_SCHEDULE_DEACTIVATE_FAILURE()
    );
    this.bootstrapService.manageModal('#propertyChargeScheduleListInfoModal', ModalActions.Show);
  }

  private buildDeactivateRequest(schedule: PropertyChargeScheduleModel): UpdatePropertyChargeScheduleRequest {
    return {
      name: schedule.name,
      description: schedule.description || undefined,
      scheduledAmount: schedule.scheduledAmount,
      memo: schedule.memo || undefined,
      revenueAccount: schedule.revenueAccount.chartOfAccountId,
      assetAccount: schedule.assetAccount.chartOfAccountId,
      activate: false,
      memberPaysServiceFee: schedule.memberPaysServiceFee,
      scheduleFrequency: schedule.frequency,
      // The API rejects a month count on any non-Custom frequency, so don't trust the response to be clean.
      customFrequencyMonths: schedule.frequency === ScheduleFrequency.Custom ? schedule.customFrequencyMonths ?? null : null,
      firstIssuanceDate: schedule.firstIssuanceDate,
      sendInvoiceAtEndOfMonth: schedule.sendInvoiceAtEndOfMonth,
      invoiceDueNumberOfMonthsFromIssuance: schedule.invoiceDueNumberOfMonthsFromIssuance,
      invoiceDueDayOfMonth: schedule.invoiceDueDayOfMonth,
      invoiceDueAtEndOfMonth: schedule.invoiceDueAtEndOfMonth,
      scheduleEnd: schedule.scheduleEnd || undefined,
      properties: {
        useFilter: false,
        filter: null,
        selectedIds: schedule.propertyIds ?? [],
        excludedIds: null,
        note: null
      },
      bankFeeExpenseAccount: schedule.bankFeeExpenseAccount?.chartOfAccountId,
      note: undefined
    };
  }

  //#endregion Deactivate / Reactivate
}
